import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { subscribeIncidentChanges } from "@/utils/incidentEvents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const useDashboard = () => {
  const [stats, setStats] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const request = useRef(null);
  const mounted = useRef(false);
  const pending = useRef(false);
  const refresh = useCallback(async () => {
    if (!mounted.current) return;
    if (request.current) { pending.current = true; return; }
    const controller = new AbortController();
    request.current = controller;
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/statistics`, { signal: controller.signal });
      if (mounted.current && !controller.signal.aborted) { setStats(data); setError(""); }
    } catch (e) {
      if (mounted.current && !controller.signal.aborted) setError("Die Dashboard-Daten konnten nicht aktualisiert werden. Angezeigte Werte stammen vom letzten erfolgreichen Abruf.");
    } finally {
      if (request.current === controller) {
        request.current = null;
        if (mounted.current) { setLoading(false); setRefreshing(false); }
        if (pending.current && mounted.current) { pending.current = false; refresh(); }
      }
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    refresh();
    const userController = new AbortController();
    axios.get(`${API}/auth/me`, { signal: userController.signal }).then(({data}) => setUser(data)).catch(() => {});
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    const unsubscribe = subscribeIncidentChanges(refresh);
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    // UI freshness only; no background job. Includes changes made by other users/devices.
    const interval = window.setInterval(onVisible, 15000);
    return () => {
      mounted.current = false;
      pending.current = false;
      request.current?.abort();
      request.current = null;
      userController.abort();
      unsubscribe();
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(interval);
    };
  }, [refresh]);
  return { stats, user, loading, refreshing, error, refresh };
};